import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/pages/OrdersPage";
import SellerStore from "@/pages/SellerStore";
import SellerPayments from "@/pages/SellerPayments";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

function Stat({ label, value, testId }) {
  return (
    <div className="border-b sm:border-b-0 sm:border-r border-slate-100 last:border-0 px-6 py-5">
      <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">{label}</p>
      <p data-testid={testId} className="font-heading text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}

function NewProductDialog({ categories, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", brand: "", category_id: "", description: "", images: "", price: "", mrp: "", sku: "", stock: 20, shipping_fee: 0, delivery_days: 4, specs: "" });

  const submit = async () => {
    try {
      const specs = {};
      f.specs.split("\n").forEach((line) => {
        const [k, ...rest] = line.split(":");
        if (k && rest.length) specs[k.trim()] = rest.join(":").trim();
      });
      await api.post("/products", {
        title: f.title, brand: f.brand, category_id: f.category_id, description: f.description,
        images: f.images.split("\n").map((s) => s.trim()).filter(Boolean),
        specs, price: Number(f.price), mrp: f.mrp ? Number(f.mrp) : undefined,
        sku: f.sku, stock: Number(f.stock), shipping_fee: Number(f.shipping_fee), delivery_days: Number(f.delivery_days),
      });
      toast.success("Product listed with your offer");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const input = (k, label, type = "text", cls = "") => (
    <input key={k} data-testid={`np-${k}`} type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
      placeholder={label} className={`h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500 ${cls}`} />
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-product-btn" className="rounded-md bg-slate-900"><Plus className="w-4 h-4 mr-1.5" /> New product</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>List a new product</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {input("title", "Product title", "text", "col-span-2")}
          {input("brand", "Brand")}
          <select data-testid="np-category" value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value })}
            className="h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500 bg-white">
            <option value="">Select category</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {input("price", "Your price (₹)", "number")}
          {input("mrp", "MRP (₹, optional)", "number")}
          {input("sku", "SKU")}
          {input("stock", "Stock", "number")}
          {input("shipping_fee", "Shipping fee (₹)", "number")}
          {input("delivery_days", "Delivery days", "number")}
          <textarea data-testid="np-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
            placeholder="Description" rows={3} className="col-span-2 rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          <textarea data-testid="np-images" value={f.images} onChange={(e) => setF({ ...f, images: e.target.value })}
            placeholder="Image URLs (one per line)" rows={2} className="col-span-2 rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          <textarea data-testid="np-specs" value={f.specs} onChange={(e) => setF({ ...f, specs: e.target.value })}
            placeholder={"Specifications, one per line:\nBattery: 40 hours\nWarranty: 1 year"} rows={3} className="col-span-2 rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
        </div>
        <Button data-testid="np-submit" onClick={submit} disabled={!f.title || !f.brand || !f.category_id || !f.price || !f.sku} className="rounded-md bg-slate-900 mt-2">Publish listing</Button>
      </DialogContent>
    </Dialog>
  );
}

function EditOfferDialog({ offer, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ price: offer.price, mrp: offer.mrp, stock: offer.stock, shipping_fee: offer.shipping_fee, delivery_days: offer.delivery_days, active: offer.active });

  const submit = async () => {
    try {
      await api.put(`/offers/${offer.id}`, { ...f, price: Number(f.price), mrp: Number(f.mrp), stock: Number(f.stock), shipping_fee: Number(f.shipping_fee), delivery_days: Number(f.delivery_days) });
      toast.success("Offer updated");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`edit-offer-${offer.id}`} variant="outline" size="sm" className="rounded-md">Edit</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Update offer</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {[["price", "Price (₹)"], ["mrp", "MRP (₹)"], ["stock", "Stock"], ["shipping_fee", "Shipping fee (₹)"], ["delivery_days", "Delivery days"]].map(([k, label]) => (
            <label key={k} className="text-xs font-semibold text-slate-500">{label}
              <input data-testid={`edit-offer-${k}`} type="number" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
                className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
            </label>
          ))}
          <label className="text-xs font-semibold text-slate-500 flex items-end gap-2 pb-2">
            <input data-testid="edit-offer-active" type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="w-4 h-4" />
            Listing active
          </label>
        </div>
        <Button data-testid="edit-offer-save" onClick={submit} className="rounded-md bg-slate-900 mt-2">Save changes</Button>
      </DialogContent>
    </Dialog>
  );
}

const NEXT_STATUS = { CONFIRMED: "PROCESSING", PROCESSING: "SHIPPED", SHIPPED: "OUT_FOR_DELIVERY", OUT_FOR_DELIVERY: "DELIVERED" };
const NEXT_LABEL = { CONFIRMED: "Pack order", PROCESSING: "Mark shipped", SHIPPED: "Out for delivery", OUT_FOR_DELIVERY: "Mark delivered" };

export default function SellerPortal() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [dash, setDash] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [returns, setReturns] = useState([]);
  const [settlements, setSettlements] = useState(null);
  const [categories, setCategories] = useState([]);
  const [payCfg, setPayCfg] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, p, o, r, s, c, pc] = await Promise.all([
        api.get("/seller/dashboard"), api.get("/seller/products"), api.get("/seller/orders"),
        api.get("/seller/returns"), api.get("/seller/settlements"), api.get("/categories"),
        api.get("/payments/config"),
      ]);
      setDash(d.data); setProducts(p.data); setOrders(o.data); setReturns(r.data); setSettlements(s.data); setCategories(c.data);
      setPayCfg(pc.data);
    } catch (e) {
      toast.error(formatError(e));
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) return nav("/vendor/login", { state: { from: "/seller" } });
    if (user.role !== "seller" && user.role !== "admin") return nav("/sell");
    load();
  }, [user, loading, nav, load]);

  if (!dash) return <div className="max-w-7xl mx-auto px-8 mt-12"><div className="h-96 bg-slate-100 rounded-2xl animate-pulse" /></div>;

  const seller = dash.seller;

  const payFee = async () => {
    try {
      await api.post("/payments/mock", { method: "MOCK_CARD" });
      await api.post("/seller/pay-fee");
      toast.success("Platform fee paid — awaiting admission by marketplace ops");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const advance = async (so) => {
    try {
      await api.post(`/seller/orders/${so.id}/status`, { status: NEXT_STATUS[so.status] });
      toast.success(`Order → ${NEXT_STATUS[so.status].replace(/_/g, " ")}`);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const returnAction = async (r, action) => {
    try {
      await api.post(`/seller/returns/${r.id}/action`, { action });
      toast.success(`Return ${action}d`);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <div data-testid="seller-portal" className="max-w-7xl mx-auto px-4 sm:px-8 mt-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{seller.business_name}</h1>
          <div className="flex items-center gap-3 mt-1">
            <StatusBadge status={seller.status} />
            <span className="text-xs text-slate-400">Seller rating {seller.rating} · {seller.legal_entity}</span>
          </div>
        </div>
        <NewProductDialog categories={categories} onDone={load} />
      </div>

      {seller.status !== "ACTIVE" && (
        <div data-testid="seller-status-banner" className="mt-6 rounded-xl border border-slate-200 bg-slate-50 px-6 py-4 text-sm flex items-center justify-between gap-4 flex-wrap">
          {seller.status === "UNDER_REVIEW" && (
            <span className="text-amber-800">Your application is <b>under review</b> by marketplace ops. Once approved, you'll pay a one-time platform fee to get admitted.</span>
          )}
          {seller.status === "APPROVED" && (
            <div className="flex-1 min-w-[280px]">
              <span className="text-slate-700">Application approved. Pay the one-time platform fee of <b>{inr(seller.platform_fee || 0)}</b> to get admitted and start listing. <span className="text-slate-400">No commissions are charged on your sales.</span></span>
              {(payCfg?.upi_id || payCfg?.account_number) && (
                <div data-testid="fee-payment-details" className="text-xs bg-white border border-slate-200 rounded-lg p-3 mt-3 space-y-1">
                  <p className="font-bold text-slate-700">Pay to: {payCfg.payee_name}</p>
                  {payCfg.upi_id && <p>UPI: <span className="font-mono font-semibold">{payCfg.upi_id}</span></p>}
                  {payCfg.account_number && <p>Bank: {payCfg.account_name} · A/C <span className="font-mono">{payCfg.account_number}</span> · IFSC <span className="font-mono">{payCfg.ifsc}</span> · {payCfg.bank_name}</p>}
                  {payCfg.note && <p className="text-slate-400">{payCfg.note}</p>}
                </div>
              )}
            </div>
          )}
          {seller.status === "APPROVED" && (
            <Button data-testid="pay-platform-fee-btn" onClick={payFee} className="rounded-full bg-slate-900 hover:bg-slate-700 shrink-0">Pay platform fee (simulated)</Button>
          )}
          {seller.status === "FEE_PAID" && (
            <span className="text-emerald-800">Platform fee received (txn <span className="font-mono">{seller.fee_txn_id}</span>). Marketplace ops will admit your account shortly.</span>
          )}
          {["RESTRICTED", "SUSPENDED", "TERMINATED"].includes(seller.status) && (
            <span className="text-red-700">Your account is {seller.status.toLowerCase()}. Contact marketplace ops.</span>
          )}
        </div>
      )}

      <Tabs defaultValue="overview" className="mt-8">
        <TabsList data-testid="seller-tabs">
          <TabsTrigger data-testid="tab-overview" value="overview">Overview</TabsTrigger>
          <TabsTrigger data-testid="tab-catalogue" value="catalogue">Catalogue & Inventory</TabsTrigger>
          <TabsTrigger data-testid="tab-orders" value="orders">Orders {dash.orders_pending > 0 && <span className="ml-1.5 bg-blue-500 text-white text-[10px] rounded-full px-1.5">{dash.orders_pending}</span>}</TabsTrigger>
          <TabsTrigger data-testid="tab-returns" value="returns">Returns {dash.returns_pending > 0 && <span className="ml-1.5 bg-orange-500 text-white text-[10px] rounded-full px-1.5">{dash.returns_pending}</span>}</TabsTrigger>
          <TabsTrigger data-testid="tab-settlements" value="settlements">Settlements</TabsTrigger>
          <TabsTrigger data-testid="tab-store" value="store">My Store</TabsTrigger>
          <TabsTrigger data-testid="tab-payments" value="payments">Payments</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border border-slate-100 rounded-xl overflow-hidden bg-slate-50/50">
            <Stat label="Revenue" value={inr(dash.revenue)} testId="seller-revenue" />
            <Stat label="Units sold" value={dash.units} testId="seller-units" />
            <Stat label="Orders" value={dash.orders_total} testId="seller-orders-count" />
            <Stat label="Pending settlement" value={inr(dash.pending_settlement)} testId="seller-pending-settlement" />
            <Stat label="Cancel rate" value={`${dash.cancel_rate}%`} testId="seller-cancel-rate" />
            <Stat label="Rating" value={dash.rating} testId="seller-rating" />
          </div>
          <div className="grid lg:grid-cols-[1fr_360px] gap-6 mt-6">
            <div className="border border-slate-100 rounded-xl p-6">
              <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-4">Revenue (last 14 days)</p>
              {dash.revenue_series.length ? (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={dash.revenue_series}>
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => inr(v)} />
                    <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-slate-400 py-16 text-center">No sales yet — revenue appears after delivered orders.</p>}
            </div>
            <div className="border border-slate-100 rounded-xl p-6">
              <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-4">Low stock alerts</p>
              {dash.low_stock.length ? dash.low_stock.map((o) => (
                <p key={o.id} className="text-sm py-2 border-b border-slate-50 last:border-0 flex justify-between">
                  <span className="font-mono text-xs">{o.sku}</span>
                  <span className={`font-bold ${o.stock === 0 ? "text-red-500" : "text-amber-600"}`}>{o.stock} left</span>
                </p>
              )) : <p className="text-sm text-slate-400">Inventory levels are healthy.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="catalogue" className="mt-6">
          <div className="border border-slate-100 rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead><TableHead>SKU</TableHead><TableHead>Price</TableHead>
                  <TableHead>Stock</TableHead><TableHead>Delivery</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map(({ offer, product, owns_listing }) => (
                  <TableRow key={offer.id} data-testid={`seller-product-row-${offer.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-md overflow-hidden bg-slate-100">{product?.images?.[0] && <img src={product.images[0]} alt="" className="w-full h-full object-cover" />}</div>
                        <div>
                          <p className="font-semibold text-sm line-clamp-1 max-w-[260px]">{product?.title}</p>
                          <p className="text-[11px] text-slate-400">{owns_listing ? "Your listing" : "Marketplace listing"} · {product?.category_name}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{offer.sku}</TableCell>
                    <TableCell className="font-semibold">{inr(offer.price)}</TableCell>
                    <TableCell><span className={offer.stock < 5 ? "text-red-500 font-bold" : ""}>{offer.stock}</span></TableCell>
                    <TableCell className="text-sm text-slate-500">{offer.delivery_days}d · {offer.shipping_fee ? inr(offer.shipping_fee) : "Free"}</TableCell>
                    <TableCell>{offer.active ? <span className="text-emerald-600 text-xs font-bold">Live</span> : <span className="text-slate-400 text-xs font-bold">Paused</span>}</TableCell>
                    <TableCell><EditOfferDialog offer={offer} onDone={load} /></TableCell>
                  </TableRow>
                ))}
                {!products.length && (
                  <TableRow><TableCell colSpan={7} className="text-center py-16 text-slate-400 text-sm">No listings yet. Create your first product to start selling.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <div className="space-y-3">
            {orders.map((so) => (
              <div key={so.id} data-testid={`seller-order-${so.id}`} className="border border-slate-100 rounded-xl p-5 flex items-center gap-6 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <p className="font-mono text-[11px] text-slate-400">{so.shipment.tracking_id}</p>
                  <p className="text-sm font-semibold mt-1">{so.items.map((i) => `${i.title} × ${i.qty}`).join(", ")}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{new Date(so.created_at).toLocaleString("en-IN")}</p>
                </div>
                <StatusBadge status={so.status} />
                <span className="font-heading font-bold">{inr(so.subtotal)}</span>
                {NEXT_STATUS[so.status] && (
                  <Button data-testid={`advance-order-${so.id}`} size="sm" onClick={() => advance(so)} className="rounded-md bg-slate-900">
                    {NEXT_LABEL[so.status]} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                )}
              </div>
            ))}
            {!orders.length && <p className="text-center py-16 text-slate-400 text-sm border border-dashed border-slate-200 rounded-xl">No orders yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="returns" className="mt-6">
          <div className="space-y-3">
            {returns.map((r) => (
              <div key={r.id} data-testid={`seller-return-${r.id}`} className="border border-slate-100 rounded-xl p-5 flex items-center gap-6 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <p className="text-sm font-semibold">{r.items.map((i) => `${i.title} × ${i.qty}`).join(", ")}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Reason: {r.reason}</p>
                </div>
                <StatusBadge status={r.status} />
                {r.status === "REQUESTED" && (
                  <div className="flex gap-2">
                    <Button data-testid={`return-approve-${r.id}`} size="sm" onClick={() => returnAction(r, "approve")} className="rounded-md bg-slate-900">Approve</Button>
                    <Button data-testid={`return-reject-${r.id}`} size="sm" variant="outline" onClick={() => returnAction(r, "reject")} className="rounded-md">Reject</Button>
                  </div>
                )}
                {r.status === "APPROVED" && (
                  <Button data-testid={`return-refund-${r.id}`} size="sm" onClick={() => returnAction(r, "refund")} className="rounded-md bg-emerald-600 hover:bg-emerald-500">Inspect & refund</Button>
                )}
              </div>
            ))}
            {!returns.length && <p className="text-center py-16 text-slate-400 text-sm border border-dashed border-slate-200 rounded-xl">No return requests.</p>}
          </div>
        </TabsContent>

        <TabsContent value="settlements" className="mt-6">
          {settlements && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 border border-slate-100 rounded-xl overflow-hidden bg-slate-50/50 mb-6">
                <Stat label="Gross sales" value={inr(settlements.totals.gross)} testId="settle-gross" />
                <Stat label="Refunds" value={`−${inr(settlements.totals.refunds)}`} testId="settle-refunds" />
                <Stat label="Net settlement" value={inr(settlements.totals.net)} testId="settle-net" />
                <Stat label="Pending payout" value={inr(settlements.totals.pending)} testId="settle-pending" />
              </div>
              <p className="text-xs text-slate-400 mb-6 -mt-3">Hello Nanba charges no commission on your sales — the one-time platform admission fee is your only platform cost. Net = gross − refunds.</p>
              <div className="border border-slate-100 rounded-xl overflow-hidden">
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Gross</TableHead><TableHead>Net</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {settlements.rows.map((r) => (
                      <TableRow key={r.id} data-testid={`settlement-row-${r.id}`}>
                        <TableCell className="text-sm">{new Date(r.created_at).toLocaleDateString("en-IN")}</TableCell>
                        <TableCell className="text-xs font-semibold">{r.type.replace(/_/g, " ")}</TableCell>
                        <TableCell>{inr(r.gross)}</TableCell>
                        <TableCell className="font-bold">{inr(r.net)}</TableCell>
                        <TableCell><StatusBadge status={r.status} /></TableCell>
                      </TableRow>
                    ))}
                    {!settlements.rows.length && <TableRow><TableCell colSpan={6} className="text-center py-16 text-slate-400 text-sm">Settlements are created when orders are delivered.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="store" className="mt-6">
          <SellerStore sellerId={seller.id} />
        </TabsContent>

        <TabsContent value="payments" className="mt-6">
          <SellerPayments />
        </TabsContent>
      </Tabs>
    </div>
  );
}
